import React, {useState} from 'react';
import Table from 'components/ui/Table';
import Spinner from 'components/ui/Spinner';
import NumberCard from 'components/ui/NumberCard';
import RangePicker from 'components/ui/RangePicker';
import SearchContainer from 'layouts/components/Search/SearchContainer';
import {IOrder, useGetTopOrdersQuery} from 'services/orders';
import {formatDate, getFormattedTodayDate} from 'utils/intlDate';
import {useGetStatsQuery} from 'services/analytics';
import {formatCurrency} from 'utils/intlNumber';
import {Link} from 'react-router-dom';
import * as Styles from 'layouts/PrivateLayout.styles';
import {Alert, AlertTitle} from "@mui/material";

const Dashboard = () => {
  const [startDate, setStartDate] = useState(
      getFormattedTodayDate().formatString
  );
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [endDate, setEndDate] = useState(getFormattedTodayDate(14).formatString);

  const {
    data: stats,
    isLoading: isLoadingStats,
    isFetching: isFetchingStats
  } = useGetStatsQuery({
    startDate,
    endDate
  });
  const { data: topOrders, isLoading: isLoadingTopOrders } =
      useGetTopOrdersQuery({page, limit, startDate, endDate});

  const sortedOrders = topOrders?.orders?.slice().sort((a, b) => {
      const dateA = new Date(a.deliveryDate).getTime();
      const dateB = new Date(b.deliveryDate).getTime();
      return dateA - dateB;
  });

  const filteredOrders = sortedOrders?.filter((order => !order.confirmed_delivery_date))

  const columns = [
    {
      field: 'delivery_date',
      headerName: 'Delivery Date',
      render: (data: IOrder) => formatDate(data?.deliveryDate)
    },
    {
      field: 'order_number',
      headerName: 'Order Number',
      render: (data: IOrder) => (
          <Link to={`/admin/orders/${data.id}`}>
            <div className="flex items-center gap-3 underline">
              #{data?.order_number}
            </div>
          </Link>
      )
    },
    {
      field: 'name',
      headerName: 'Name',
      render: (data: IOrder) => (
            <div className="flex items-center gap-3">
              {data?.customer?.name || 'N/A'}
            </div>
      )
    },
    {
      field: 'location',
      headerName: 'Location',
      render: (data: IOrder) => data?.locations?.name 
    },
    {
      field: 'email',
      headerName: 'Email address',
      render: (data: IOrder) => data?.customer?.email || 'N/A'
    },
    {
      field: 'total',
      headerName: 'Total',
      sortable: false,
      render: (data: IOrder) => formatCurrency(Number(data?.total_price))
    }
  ];

  const isDataProcessing = topOrders?.orders?.some(order => !order.customer);

  return (
      <div>
        <SearchContainer />

        <Styles.PageTitleContainer>
          Dashboard
          <Styles.PageTitleContainerButtons>
            <RangePicker
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
            />
          </Styles.PageTitleContainerButtons>
        </Styles.PageTitleContainer>

        <Styles.SectionBoxSpace>
          <Styles.SectionThreeColumns>
            <NumberCard
                isCurrency
                id="Sales"
                title="Sales"
                number={stats?.total_sales}
                isLoading={isLoadingStats || isFetchingStats}
            />
            <NumberCard
                id="bookings"
                title="Bookings"
                number={stats?.total_bookings}
                isLoading={isLoadingStats || isFetchingStats}
            />
            <NumberCard
                id="customers"
                title="Customers"
                number={stats?.total_customers}
                isLoading={isLoadingStats || isFetchingStats}
            />
          </Styles.SectionThreeColumns>
        </Styles.SectionBoxSpace>

        <Styles.PageTitleContainer>Upcoming Deliveries</Styles.PageTitleContainer>

        {isDataProcessing && (
            <Alert className="mb-4">
              <AlertTitle>Data Processing</AlertTitle>
              Some order data is still being processed. Customer information may be temporarily unavailable.
            </Alert>
        )}

        {isLoadingTopOrders ? (
            <Spinner />
        ) : (
            <Table
                columns={columns}
                rows={filteredOrders || []}
                emptyTitleText="No orders yet"
                emptyText="You haven't received any orders yet."
                isFetching={isLoadingTopOrders}
            />
        )}
      </div>
  );
};

export default Dashboard;
